// Calendario de obra: un mes por pantalla (lista por semana en el celular). Cada día muestra el tiempo,
// las horas trabajadas, el pico de personas, las máquinas que se vieron, las visitas de camión y la foto
// con más movimiento. Clic en un día = "Diario del día" con la línea de tiempo y el texto para compartir.
// Todo sale de las detecciones que ya carga el Dashboard IA (misma caché), sin costo extra.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { loadDays, listDays, addDays, todayKey, inWork, photoUrl, fmtHM, fmtDM, fmtKey, dayKey, dayStartMs, classInfo, DEFAULT_CFG, loadHelmetFixes } from './iaData';
import { resolveLocation, loadWeather, wxKind, WX_KINDS } from './weather';
import { UbicacionObraModal, CambiarUbicacionLink } from '../../../obras/components/UbicacionObra';
import { anchoObjetivo, cargarImagen, imagenLista, photoUrlLista, precargar, olvidarUrl, ORIGINAL } from './fotos';
import { WX_ICONS, skyCss } from './clima/Sky';
import { byDay, daySummary, markFixedSpots, loadMarks, saveMark, marksCsv, diarioText, zoneChange, VEHICLE_LABEL } from './entregas';
import { T, Btn, Chip, inputStyle } from './ui';
import ChatObra from './ChatObra';
import { cargarEntregas, guardarMarca, pulirMemoria, titulo as tituloV3, MATERIAL_ICON, MATERIAL_LABEL } from './entregasV3';
import { memoriaDia, diaAnterior } from './memoria';

// Entregas: con el modelo nuevo (iaV3, RF-DETR) se ven camiones, mixers y los materiales que quedaron. Si la cámara
// todavía no tiene iaV3 se usa la heurística vieja (camión en 2 fotos seguidas). Para apagarlas: REACT_APP_CALENDARIO_ENTREGAS=0
const MOSTRAR_ENTREGAS = process.env.REACT_APP_CALENDARIO_ENTREGAS !== '0';

const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const DOWS = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'];
const DOW_LONG = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const monthOf = (key) => key.slice(0, 7);
const shiftMonth = (ym, n) => { const d = new Date(Date.parse(ym + '-01T12:00:00Z')); d.setUTCMonth(d.getUTCMonth() + n); return d.toISOString().slice(0, 7); };
const lastOfMonth = (ym) => addDays(shiftMonth(ym, 1) + '-01', -1);
const dowOf = (key) => new Date(key + 'T12:00:00Z').getUTCDay();
const isWeekend = (key) => [0, 6].includes(dowOf(key));
const vColor = (cls) => classInfo(cls).color;
const vLabel = (cls) => VEHICLE_LABEL[cls] || cls;
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

function useNarrow(px = 720) {
  const q = `(max-width:${px}px)`;
  const [m, setM] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const f = () => setM(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', f) : mq.addListener(f);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', f) : mq.removeListener(f));
  }, [q]);
  return m;
}

// Foto firmada recién cuando aparece en pantalla (un mes = 30 fotos, no se firman todas de golpe), en el tamaño
// justo para el lugar que ocupa (miniatura de 320/640/1280 px, ver fotos.js). Mientras llega la nueva se sigue viendo
// la anterior (o la de 320 px, borrosa): nunca queda en blanco.
// crop = recorte de la foto (en píxeles de la foto) para acercar la zona del camión
// alta = se permite la original 4K (Visor acercado): primero se ve la de 1280 y la 4K se cambia en segundo plano
// prioridad = 'alta' (foto principal) | 'baja'
export function Photo({ photo, style, box, boxes, zone, crop, children, onClick, alta = false, prioridad }) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  const [cssW, setCssW] = useState(0);
  const [vista, setVista] = useState(null); // { url, photo, crop, w } lo que se ve ahora
  const [falla, setFalla] = useState(null); // s3 de la foto que no cargó ni en miniatura ni original
  useEffect(() => {
    if (!ref.current || vis) return undefined;
    if (!('IntersectionObserver' in window)) { setVis(true); return undefined; }
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setVis(true); io.disconnect(); } }, { rootMargin: '200px' });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [vis]);
  useEffect(() => {
    // Ancho real del contenedor (redondeado para no volver a pedir por un par de píxeles)
    const el = ref.current;
    if (!el) return undefined;
    const medir = () => setCssW(Math.ceil(el.getBoundingClientRect().width / 40) * 40);
    medir();
    if (!('ResizeObserver' in window)) return undefined;
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const cropKey = crop ? `${Math.round(crop.x1)},${Math.round(crop.y1)},${Math.round(crop.x2)},${Math.round(crop.y2)}` : '';
  // La foto principal siempre en 1280 (se ve grande y es la que se abre en el Visor)
  const w = prioridad === 'alta' && !alta ? Math.max(1280, anchoObjetivo(cssW, photo, crop)) : anchoObjetivo(cssW, photo, crop, { alta });
  useEffect(() => {
    if (!vis || !photo || !cssW) return undefined;
    let alive = true;
    const mostrar = (url, ancho) => alive && setVista((v) => (v && v.photo === photo && v.cropKey === cropKey && v.w >= ancho ? v : { url, photo, crop, cropKey, w: ancho }));
    // Ya bajada y decodificada: al instante
    const lista = photoUrlLista(photo, w);
    if (lista && imagenLista(lista)) { mostrar(lista, w); return () => { alive = false; }; }
    // Mientras tanto la de 320 px (≈10 KB) si es que se va a pedir una más grande. Con alta se pasa por la de 1280.
    const pasos = [...new Set([w > 320 ? 320 : null, alta && w > 1280 ? 1280 : null, w].filter(Boolean))];
    let vista1 = false, deOriginal = false;
    const intentos = pasos.map((ancho) => photoUrl(photo, { w: ancho, prioridad: prioridad === 'alta' ? 'alta' : 'normal' })
      .then((u) => cargarImagen(u, { alta: prioridad === 'alta', baja: prioridad === 'baja' }).then(() => { vista1 = true; mostrar(u, ancho); }))
      .catch((e) => { olvidarUrl(photo, ancho); throw e; }));
    // Nunca gris: si ninguna miniatura cargó (o no llegó nada en 6 s) se muestra la original; si tampoco, un aviso.
    const irOriginal = () => {
      if (!alive || vista1 || deOriginal) return;
      deOriginal = true;
      photoUrl(photo)
        .then((u) => cargarImagen(u).then(() => mostrar(u, ORIGINAL)))
        .catch(() => { olvidarUrl(photo); if (alive) setFalla(photo.s3); });
    };
    Promise.allSettled(intentos).then((r) => { if (r.every((x) => x.status === 'rejected')) irOriginal(); });
    const t = setTimeout(irOriginal, 6000);
    return () => { alive = false; clearTimeout(t); };
  }, [vis, photo, cropKey, w, cssW > 0]); // eslint-disable-line react-hooks/exhaustive-deps
  const actual = !!vista && vista.photo === photo && vista.cropKey === cropKey;
  const url = vista && vista.url;
  // Las cajas y el recorte se dibujan sobre la foto que se está viendo (la anterior, hasta que llega la nueva)
  const vp = vista ? vista.photo : photo;
  const vc = vista ? vista.crop : crop;
  const f = vc || (vp ? { x1: 0, y1: 0, x2: vp.w, y2: vp.h } : null);
  const fw = f ? f.x2 - f.x1 : 1, fh = f ? f.y2 - f.y1 : 1;
  const pct = (b) => ({ left: `${(100 * (b.x1 - f.x1)) / fw}%`, top: `${(100 * (b.y1 - f.y1)) / fh}%`, width: `${(100 * (b.x2 - b.x1)) / fw}%`, height: `${(100 * (b.y2 - b.y1)) / fh}%` });
  const borrosa = !actual || (vista.w < w && vista.w <= 320 && w >= 640);
  const imgStyle = {
    ...(vc && vp ? { position: 'absolute', ...pct({ x1: 0, y1: 0, x2: vp.w, y2: vp.h }), maxWidth: 'none', display: 'block' } : { width: '100%', height: '100%', objectFit: 'cover', display: 'block' }),
    filter: borrosa ? (actual ? 'blur(3px)' : 'saturate(.6) brightness(.92)') : 'none',
    transition: 'filter .2s',
  };
  return (
    <div ref={ref} onClick={onClick} data-foto={photo ? photo.s3 : undefined} data-ancho={actual ? vista.w : undefined} style={{ position: 'relative', aspectRatio: '16 / 9', background: '#e5e7eb', borderRadius: 8, overflow: 'hidden', ...style }}>
      {url && <img src={url} alt="" decoding="async" style={imgStyle} />}
      {!url && photo && falla === photo.s3 && <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', fontSize: 11, color: '#6b7280', textAlign: 'center', padding: 4 }}>No se pudo cargar la foto</div>}
      {actual && box && photo && <div style={{ position: 'absolute', border: `2px solid ${box.color}`, borderRadius: 3, boxShadow: '0 0 0 1px rgba(255,255,255,.7)', ...pct(box) }} />}
      {actual && photo && (boxes || []).map((b, j) => <div key={j} style={{ position: 'absolute', border: `2px solid ${b.color}`, borderRadius: 3, boxShadow: '0 0 0 1px rgba(255,255,255,.7)', ...pct(b) }} />)}
      {actual && zone && photo && <div style={{ position: 'absolute', border: '2px dashed rgba(255,255,255,.9)', borderRadius: 4, ...pct(zone) }} />}
      {children}
    </div>
  );
}

// Visor a pantalla completa de una visita: Antes / Llegada / Después grandes, con "Ampliar zona"
// (recorte alrededor del camión o de la zona) y zoom con la rueda o pellizcando.
export function Visor({ fotos, inicio, zoomZona, onClose }) {
  const [i, setI] = useState(inicio);
  const [ampliar, setAmpliar] = useState(!!zoomZona);
  const [esc, setEsc] = useState(1);
  const [origen, setOrigen] = useState('50% 50%');
  const ir = (d) => { setEsc(1); setI((x) => (x + d + fotos.length) % fotos.length); };
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') onClose(); if (e.key === 'ArrowRight') ir(1); if (e.key === 'ArrowLeft') ir(-1); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  });
  const f = fotos[i];
  // La foto anterior y la siguiente quedan listas (1280 px, prioridad baja) para que ← → sea instantáneo
  useEffect(() => {
    if (fotos.length > 1) precargar([fotos[(i + 1) % fotos.length], fotos[(i - 1 + fotos.length) % fotos.length]].filter((x) => x && x.photo).map((x) => [x.photo, 1280]));
  }, [i, fotos]);
  const zz = f.zoom !== undefined ? f.zoom : zoomZona; // cada foto puede traer su propia zona (resultados de búsqueda)
  const rueda = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setOrigen(`${(100 * (e.clientX - r.left)) / r.width}% ${(100 * (e.clientY - r.top)) / r.height}%`);
    setEsc((s) => Math.min(5, Math.max(1, s * (e.deltaY < 0 ? 1.25 : 0.8))));
  };
  const boton = { background: 'rgba(255,255,255,.12)', color: '#fff', border: '1px solid rgba(255,255,255,.25)', borderRadius: 999, padding: '6px 12px', fontSize: 13, cursor: 'pointer' };
  return createPortal(
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgb(2,6,23)', zIndex: 1400, display: 'flex', flexDirection: 'column', padding: 16, gap: 10 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', color: '#fff' }}>
        {fotos.length <= 4 ? fotos.map((x, j) => (
          <button key={x.label} type="button" onClick={() => { setEsc(1); setI(j); }} style={{ ...boton, background: j === i ? '#2563eb' : boton.background, borderColor: j === i ? '#2563eb' : boton.border }}>
            {x.label}{x.photo ? ` · ${fmtHM(x.photo.ms)}` : ''}
          </button>
        )) : (
          // Muchas fotos (resultados de búsqueda): contador en vez de un botón por foto
          <span style={{ ...boton, background: '#2563eb', borderColor: '#2563eb' }}>{f.label} · {i + 1} de {fotos.length}</span>
        )}
        {zz && <button type="button" onClick={() => { setEsc(1); setAmpliar(!ampliar); }} style={{ ...boton, background: ampliar ? '#10b981' : boton.background }}>{ampliar ? '🔍 Zona ampliada' : '🖼️ Foto completa'}</button>}
        <span style={{ fontSize: 12, opacity: 0.7 }}>Rueda del mouse para acercar · ← → para cambiar de foto</span>
        <button type="button" onClick={onClose} aria-label="Cerrar" style={{ ...boton, marginLeft: 'auto', fontSize: 16 }}>✕</button>
      </div>
      <div onClick={(e) => e.stopPropagation()} onWheel={rueda} style={{ flex: 1, minHeight: 0, display: 'grid', placeItems: 'center', overflow: 'hidden' }}>
        {f.photo ? (
          <div style={{ width: 'min(100%, calc((100vh - 120px) * 16 / 9))', transform: `scale(${esc})`, transformOrigin: origen, transition: 'transform .12s' }}>
            <Photo photo={f.photo} box={f.box} boxes={f.boxes} zone={ampliar ? null : f.zone} crop={ampliar ? zz : null} alta={esc > 1.2 || (ampliar && !!zz)} prioridad="alta" style={{ borderRadius: 10 }} />
          </div>
        ) : <div style={{ color: '#fff' }}>Sin foto</div>}
      </div>
      {fotos.length > 1 && (
        <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', justifyContent: 'center', gap: 10 }}>
          <button type="button" onClick={() => ir(-1)} style={boton}>‹ Anterior</button>
          <button type="button" onClick={() => ir(1)} style={boton}>Siguiente ›</button>
        </div>
      )}
    </div>,
    document.body,
  );
}

const WxBadge = ({ wx, size = 22 }) => (wx ? (
  <span title={`${wx.label} · ${wx.detail}`} style={{ width: size + 4, height: size + 4, borderRadius: 999, background: skyCss(wx.kind), display: 'inline-grid', placeItems: 'center', flex: 'none' }}>
    <img src={WX_ICONS[wx.kind]} alt={wx.label} width={size} height={size} />
  </span>
) : null);

const dayWx = (w) => {
  if (!w) return null;
  const kind = wxKind(w.c, w.p, { heavy: 10, minMm: 1 });
  const bits = [w.p >= 0.1 ? `lluvia ${String(+w.p.toFixed(1)).replace('.', ',')} mm` : 'sin lluvia', w.max != null && `máx ${Math.round(w.max)}°`, w.min != null && `mín ${Math.round(w.min)}°`];
  return { kind, ...WX_KINDS[kind], p: w.p, detail: bits.filter(Boolean).join(' · ') };
};

// Página propia (menú Obra > Calendario). Usa la misma jornada y ajustes que el Dashboard IA de esa cámara.
const loadCfg = (cam) => {
  try { return { ...DEFAULT_CFG, ...JSON.parse(localStorage.getItem('iaDash:' + cam) || '{}') }; } catch (e) { return DEFAULT_CFG; }
};

export default function CalendarioObra(props) {
  const { cam, name } = props;
  const cfg = useMemo(() => { loadHelmetFixes(cam); return props.cfg || loadCfg(cam); }, [cam, props.cfg]);
  return (
    <div style={{ background: T.bg, color: T.text, borderRadius: 24, padding: 'clamp(12px, 2vw, 22px)' }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 22, fontWeight: 700 }}>Calendario de obra <span style={{ fontWeight: 400, fontStyle: 'italic', color: T.muted }}>{name || cam}</span></div>
        <div style={{ fontSize: 12, color: T.muted }}>Qué pasó cada día: jornada, personas, máquinas y clima. Tocá un día para ver su diario.</div>
      </div>
      <section style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 20, padding: 'clamp(10px, 2vw, 16px)', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
        <Calendario {...props} cfg={cfg} />
      </section>
      <p style={{ fontSize: 11, color: T.muted, marginTop: 12, lineHeight: 1.5 }}>
        Datos de las fotos de la cámara (aprox. una cada 10 minutos), hora de obra (Argentina). {cfg.onlyWork ? `Se cuentan las fotos de ${cfg.workFrom} a ${cfg.workTo} h (se cambia en Dashboard IA › Ajustes).` : 'Se cuentan las fotos de todo el día.'}
        {MOSTRAR_ENTREGAS && ' Entregas: la IA mira una foto cada 20 minutos y marca cuándo llega un camión, volquete o mixer y qué material nuevo quedó (pallets, bolsones, madera, hierros, arena, piedra). Puede equivocarse: confirmá o descartá cada una y así aprende.'}
      </p>
    </div>
  );
}

function Calendario({ cam, customerId, cfg, name, coords, feedId }) {
  const narrow = useNarrow();
  const [month, setMonth] = useState(() => monthOf(todayKey()));
  const [photos, setPhotos] = useState([]);
  const [progress, setProgress] = useState(null);
  const [withPickups, setWithPickups] = useState(() => { try { return localStorage.getItem('iaDash:entregas:camionetas') === '1'; } catch (e) { return false; } });
  const [marks, setMarks] = useState(() => loadMarks(cam));
  const [open, setOpen] = useState(null);
  const [loc, setLoc] = useState(undefined);
  const [cambiarUbi, setCambiarUbi] = useState(false);
  const [wxDays, setWxDays] = useState(null);
  const [chat, setChat] = useState(false);
  // Una sola puerta: "Preguntale a tu obra" vive en Licha (el widget de ayuda). El modal propio queda solo
  // si el widget no cargó (p. ej. vista previa local sin widget).
  const abrirPregunta = () => {
    const w = typeof window !== 'undefined' ? window.LisualWidget : null;
    if (w && typeof w.preguntar === 'function') w.preguntar({ feedId, feedName: name });
    else setChat(true);
  };
  useEffect(() => setMarks(loadMarks(cam)), [cam]);
  // Entregas del modelo nuevo (iaV3). Lo que el cliente ya marcó viene del servidor y se suma a lo local.
  const [v3, setV3] = useState({ fuente: null, porDia: new Map() });
  useEffect(() => { try { localStorage.setItem('iaDash:entregas:camionetas', withPickups ? '1' : '0'); } catch (e) { /* */ } }, [withPickups]);

  const today = todayKey();
  const fromKey = month + '-01';
  const toKey = lastOfMonth(month) < today ? lastOfMonth(month) : today;
  const days = useMemo(() => (fromKey <= toKey ? listDays(fromKey, toKey) : []), [fromKey, toKey]);

  useEffect(() => {
    let alive = true;
    setPhotos([]);
    if (!days.length) { setProgress(null); return undefined; }
    setProgress({ done: 0, total: days.length });
    loadDays(cam, customerId, days, (done, total, partial) => { if (!alive) return; setProgress({ done, total }); if (partial) setPhotos(partial); }, { feedId })
      .then((ph) => { if (alive) { setPhotos(ph); setProgress(null); } });
    return () => { alive = false; };
  }, [cam, customerId, days, feedId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Último día del mes anterior: la foto de "Antes" de una entrega que llegó con la primera foto del día 1
  const [prevPhotos, setPrevPhotos] = useState([]);
  useEffect(() => {
    let alive = true;
    setPrevPhotos([]);
    if (!days.length) return undefined;
    loadDays(cam, customerId, [addDays(fromKey, -1)], () => {}, { feedId }).then((ph) => alive && setPrevPhotos(ph || [])).catch(() => {});
    return () => { alive = false; };
  }, [cam, customerId, days, feedId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let alive = true;
    if (!MOSTRAR_ENTREGAS || !days.length) return undefined;
    cargarEntregas(feedId, fromKey, toKey).then((r) => {
      if (!alive) return;
      setV3(r);
      const server = {};
      for (const vs of r.porDia.values()) for (const v of vs) if (v.marca) server[v.id] = { estado: v.marca.estado, nota: v.marca.nota };
      if (Object.keys(server).length) setMarks((m) => ({ ...server, ...m }));
    });
    return () => { alive = false; };
  }, [feedId, fromKey, toKey, days.length]);

  useEffect(() => { resolveLocation({ coords, feedId, cam }).then(setLoc); }, [coords, feedId, cam]);
  useEffect(() => {
    if (!loc || !days.length) return undefined;
    let alive = true;
    loadWeather(loc, fromKey, toKey).then((h) => alive && setWxDays(h.days)).catch(() => alive && setWxDays(null));
    return () => { alive = false; };
  }, [loc, fromKey, toKey, days.length]);

  const opts = useMemo(() => ({ withPickups }), [withPickups]);
  const sums = useMemo(() => {
    const m = new Map();
    const todas = prevPhotos.length ? [...prevPhotos, ...photos] : photos;
    const g = byDay(photos.filter((p) => inWork(p.ms, cfg)));
    for (const [k, ph] of g) m.set(k, daySummary(ph, cfg, opts));
    markFixedSpots(m);
    if (!MOSTRAR_ENTREGAS) for (const d of m.values()) { d.visits = []; d.parked = []; }
    else if (v3.fuente === 'iaV3') {
      // Desde que la cámara tiene iaV3 mandan esas entregas; antes queda la heurística como respaldo.
      const desde = v3.analizadoDesde ? new Date(v3.analizadoDesde - 3 * 3600e3).toISOString().slice(0, 10) : '';
      for (const [k, d] of m) {
        if (k < desde) continue;
        d.visits = (v3.porDia.get(k) || []).filter((v) => v.tipo === 'material' || withPickups || v.cls !== 'Camioneta').map((v) => conAntes(v, todas));
        d.parked = []; d.fuente = 'iaV3';
      }
    }
    return m;
  }, [photos, prevPhotos, cfg, opts, v3, withPickups]);

  // Para revisar los datos en local (cuántas visitas, de qué tipo): window.__calendarioObra
  useEffect(() => { if (process.env.NODE_ENV === 'development') window.__calendarioObra = { cam, month, sums, zoneChange, url: photoUrl }; }, [cam, month, sums]);

  const totals = useMemo(() => {
    let worked = 0, visits = 0, mixers = 0, confirmed = 0;
    for (const s of sums.values()) {
      if (s.first != null) worked += 1;
      const v = s.visits.filter((x) => marks[x.id]?.estado !== 'no');
      visits += v.length; mixers += v.filter((x) => x.cls === 'Mixer').length;
      confirmed += v.filter((x) => marks[x.id]?.estado === 'si').length;
    }
    return { worked, visits, mixers, confirmed };
  }, [sums, marks]);

  const exportCsv = () => {
    const blob = new Blob(['﻿' + marksCsv(cam)], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `entregas-${cam}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  };
  const onMark = (ev, patch) => {
    const all = saveMark(cam, ev, patch);
    setMarks(all);
    // iaV3: también al servidor (entregasFeedback), así lo ve el resto del equipo y sirve para reentrenar.
    if (ev.v3) guardarMarca(feedId, ev.id, { estado: all[ev.id]?.estado ?? null, nota: all[ev.id]?.nota || '' }).catch(() => {});
  };
  const conV3 = [...sums.values()].some((s) => s.fuente === 'iaV3');
  const entregasChat = useMemo(() => (MOSTRAR_ENTREGAS ? entregasParaChat(sums, marks) : null), [sums, marks]);
  // Memoria descriptiva de cada día (plantilla con los números; se pule con IA solo al abrir el día)
  const memorias = useMemo(() => {
    const m = new Map();
    for (const [k, s] of sums) {
      const prevK = diaAnterior(k, sums);
      m.set(k, memoriaDia({ k, s, prevK, prev: prevK ? sums.get(prevK) : null, wx: wxDays && wxDays.get(k), res: v3.resumenes && v3.resumenes[k], resPrev: prevK && v3.resumenes ? v3.resumenes[prevK] : null, marks }));
    }
    return m;
  }, [sums, wxDays, v3, marks]);

  const lead = (dowOf(month + '-01') + 6) % 7;
  const allDays = listDays(month + '-01', lastOfMonth(month));
  const canNext = shiftMonth(month, 1) <= monthOf(today);

  return (
    <div>
      <style>{`
        .cal-grid{display:grid;grid-template-columns:repeat(7,minmax(0,1fr));gap:6px}
        .cal-cell{border:1px solid ${T.line};border-radius:12px;padding:6px;min-height:132px;display:flex;flex-direction:column;gap:4px;background:#fff;cursor:pointer;text-align:left;font:inherit;color:inherit;min-width:0;transition:box-shadow .15s,border-color .15s}
        .cal-cell:hover{border-color:${T.blue};box-shadow:0 4px 14px rgba(37,99,235,.12)}
        .cal-cell.we{background:#fafafa}
        .cal-cell.off{background:#f3f4f6;cursor:default;color:#9ca3af}
        .cal-cell.off:hover{border-color:${T.line};box-shadow:none}
        .cal-cell.fut{background:transparent;border-style:dashed}
        @media (max-width:1100px){.cal-cell{min-height:118px}.cal-hide-md{display:none!important}}
      `}</style>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Btn onClick={() => setMonth(shiftMonth(month, -1))} title="Mes anterior">‹</Btn>
          <div style={{ fontSize: 18, fontWeight: 700, minWidth: 150, textAlign: 'center' }}>{cap(MONTHS[+month.slice(5, 7) - 1])} {month.slice(0, 4)}</div>
          <Btn disabled={!canNext} onClick={() => canNext && setMonth(shiftMonth(month, 1))} title="Mes siguiente">›</Btn>
          {month !== monthOf(today) && <Btn onClick={() => setMonth(monthOf(today))}>Hoy</Btn>}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', fontSize: 13 }}>
          <button type="button" data-obra-chat onClick={abrirPregunta} title="Preguntale a tu obra: hablá o escribí y te contesto con los datos de la cámara"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 999, border: 0, background: T.blue, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', boxShadow: '0 2px 8px rgba(37,99,235,.3)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 10a7 7 0 0 0 14 0" /><line x1="12" y1="17" x2="12" y2="22" /></svg>
            Preguntale a tu obra
          </button>
          <Chip color={T.green}>{totals.worked} {totals.worked === 1 ? 'día trabajado' : 'días trabajados'}</Chip>
          {MOSTRAR_ENTREGAS && <>
          <Chip color="#c2410c" title={conV3 ? 'Detectadas con el modelo nuevo de IA (camiones, mixers y materiales)' : 'Camiones vistos en dos fotos seguidas'}>🚚 {totals.visits} {conV3 ? (totals.visits === 1 ? 'entrega' : 'entregas') : (totals.visits === 1 ? 'posible entrega' : 'posibles entregas')}</Chip>
          {totals.mixers > 0 && <Chip color="#a855f7">{totals.mixers} {totals.mixers === 1 ? 'mixer' : 'mixers'}</Chip>}
          {totals.confirmed > 0 && <Chip color={T.blue}>✓ {totals.confirmed} {totals.confirmed === 1 ? 'confirmada' : 'confirmadas'}</Chip>}
          <Chip color="#6366f1" on={withPickups} onClick={() => setWithPickups(!withPickups)} title="Contar también las camionetas que entran y salen (fletes chicos)">{withPickups ? '☑' : '☐'} Incluir camionetas</Chip>
          <Btn onClick={exportCsv} title="Descargar las entregas que marcaste (CSV para Excel)">⬇ Exportar entregas</Btn>
          </>}
        </div>
      </div>

      {progress && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: T.muted, marginBottom: 4 }}>Cargando {progress.done} de {progress.total} días…</div>
          <div style={{ height: 4, background: T.line, borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: `${(100 * progress.done) / Math.max(1, progress.total)}%`, height: '100%', background: T.blue, transition: 'width .2s' }} />
          </div>
        </div>
      )}
      {loc === null && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '10px 12px', marginBottom: 10, fontSize: 13, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 200 }}><b>¿Dónde está tu obra?</b> Ubicala en el mapa para ver el clima de cada día.</span>
          <Btn active onClick={() => setCambiarUbi(true)}>📍 Ubicar obra</Btn>
        </div>
      )}
      {loc && feedId && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12, color: T.muted, marginBottom: 8 }}>
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70vw' }}>Clima de: {loc.label}</span>
          <CambiarUbicacionLink onClick={() => setCambiarUbi(true)} />
        </div>
      )}
      <UbicacionObraModal open={cambiarUbi} onClose={() => setCambiarUbi(false)} feedId={feedId} cam={cam}
        initial={loc || null} direccion={loc?.direccion || ''} onSaved={(l) => { setCambiarUbi(false); setLoc(l); }} />

      {narrow ? (
        <WeekList days={allDays} today={today} sums={sums} wxDays={wxDays} marks={marks} onOpen={setOpen} />
      ) : (
        <>
          <div className="cal-grid" style={{ marginBottom: 6 }}>
            {DOWS.map((d) => <div key={d} style={{ fontSize: 11, fontWeight: 600, color: T.muted, textTransform: 'uppercase', letterSpacing: '.04em', paddingLeft: 6 }}>{d}</div>)}
          </div>
          <div className="cal-grid">
            {Array.from({ length: lead }, (_, i) => <div key={'x' + i} />)}
            {allDays.map((k) => <DayCell key={k} k={k} future={k > today} s={sums.get(k)} wx={dayWx(wxDays && wxDays.get(k))} marks={marks} onOpen={setOpen} memo={memorias.get(k)} />)}
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11, color: T.muted, marginTop: 10, alignItems: 'center' }}>
        <span>🕒 primera y última persona en obra</span><span>👷 pico de personas</span>{MOSTRAR_ENTREGAS && <span>🚚 {conV3 ? 'entregas (camión, mixer o material nuevo)' : 'visitas de camión (posibles entregas)'}</span>}
        <span>Máquinas vistas: 🚜 excavadora · 🌀 mixer · 🚚 camión</span><span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#f3f4f6', border: `1px solid ${T.line}` }} /> sin fotos</span>
      </div>

      {chat && (
        <ChatObra cam={cam} customerId={customerId} feedId={feedId} name={name} photos={photos} cfg={cfg} wxDays={wxDays} entregas={entregasChat}
          fromKey={fromKey} toKey={toKey} today={today} loading={!!progress} onClose={() => setChat(false)} />
      )}

      {open && sums.get(open) && (
        <DiarioDia k={open} s={sums.get(open)} wx={dayWx(wxDays && wxDays.get(open))} name={name} marks={marks} onMark={onMark} cfg={cfg} memo={memorias.get(open)} feedId={feedId}
          onClose={() => setOpen(null)}
          onPrev={() => { const ks = [...sums.keys()].sort().filter((x) => x < open); if (ks.length) setOpen(ks[ks.length - 1]); }}
          onNext={() => { const ks = [...sums.keys()].sort().filter((x) => x > open); if (ks.length) setOpen(ks[0]); }} />
      )}
    </div>
  );
}

function DayCell({ k, s, wx, marks, onOpen, future, memo }) {
  const off = !s || future;
  const vis = s ? s.visits.filter((v) => marks[v.id]?.estado !== 'no') : [];
  const mixers = vis.filter((v) => v.cls === 'Mixer').length;
  return (
    <button type="button" data-cal-day={k} className={`cal-cell${off ? ' off' : isWeekend(k) ? ' we' : ''}${future ? ' fut' : ''}`} onClick={() => !off && onOpen(k)} disabled={off}
      title={off ? (future ? '' : 'La cámara no mandó fotos ese día') : memo ? memo.texto : 'Ver el diario del día'}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: off ? '#9ca3af' : k === todayKey() ? T.blue : T.text }}>{+k.slice(8, 10)}</span>
        {!future && <WxBadge wx={wx} size={18} />}
      </div>
      {s && !future && (
        <>
          <Photo photo={s.best} style={{ borderRadius: 6 }}>
            {vis.length > 0 && (
              <span style={{ position: 'absolute', right: 4, bottom: 4, background: mixers ? '#a855f7' : '#c2410c', color: '#fff', borderRadius: 999, padding: '1px 7px', fontSize: 11, fontWeight: 700, boxShadow: '0 1px 3px rgba(0,0,0,.3)' }}>🚚 {vis.length}</span>
            )}
          </Photo>
          <div style={{ fontSize: 11, color: T.muted, lineHeight: 1.35, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {s.first != null ? <>🕒 {fmtHM(s.first)}–{fmtHM(s.last)}</> : 'Sin personas'}
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11, color: T.text, flexWrap: 'wrap' }}>
            {s.peak > 0 && <span title="Pico de personas">👷 {s.peak}</span>}
            {s.machines.map((m) => <span key={m.key} title={`${m.label} en obra`} className={m.key === 'camion' ? 'cal-hide-md' : ''}>{m.icon}</span>)}
          </div>
        </>
      )}
    </button>
  );
}

function WeekList({ days, today, sums, wxDays, marks, onOpen }) {
  const shown = days.filter((k) => k <= today).reverse();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {shown.map((k, i) => {
        const s = sums.get(k);
        const wx = dayWx(wxDays && wxDays.get(k));
        const vis = s ? s.visits.filter((v) => marks[v.id]?.estado !== 'no') : [];
        const newWeek = i === 0 || dowOf(k) === 0;
        return (
          <React.Fragment key={k}>
            {newWeek && <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '.04em', marginTop: i ? 6 : 0 }}>Semana del {fmtKey(weekStart(k)).slice(0, 5)}</div>}
            <button type="button" data-cal-day={k} onClick={() => s && onOpen(k)} disabled={!s}
              style={{ display: 'flex', gap: 10, alignItems: 'center', textAlign: 'left', font: 'inherit', color: 'inherit', padding: 8, borderRadius: 12, border: `1px solid ${T.line}`, background: !s ? '#f3f4f6' : isWeekend(k) ? '#fafafa' : '#fff', cursor: s ? 'pointer' : 'default', opacity: s ? 1 : 0.75 }}>
              <div style={{ width: 92, flex: 'none' }}>
                {s ? <Photo photo={s.best} style={{ borderRadius: 8 }} /> : <div style={{ aspectRatio: '16 / 9', borderRadius: 8, background: '#e5e7eb' }} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: 14 }}>
                  <span style={{ textTransform: 'capitalize' }}>{DOW_LONG[dowOf(k)].slice(0, 3)} {+k.slice(8, 10)}</span>
                  <WxBadge wx={wx} size={16} />
                </div>
                <div style={{ fontSize: 12, color: T.muted }}>
                  {!s ? 'Sin fotos' : s.first != null ? `🕒 ${fmtHM(s.first)}–${fmtHM(s.last)} · 👷 ${s.peak}` : 'Sin personas'}
                  {s && s.machines.length > 0 && <> · {s.machines.map((m) => m.icon).join(' ')}</>}
                </div>
              </div>
              {vis.length > 0 && <span style={{ background: vis.some((v) => v.cls === 'Mixer') ? '#a855f7' : '#c2410c', color: '#fff', borderRadius: 999, padding: '2px 8px', fontSize: 12, fontWeight: 700, flex: 'none' }}>🚚 {vis.length}</span>}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}
const weekStart = (k) => addDays(k, -((dowOf(k) + 6) % 7));

// ---------------------------------------------------------------------------
// Diario del día
// ---------------------------------------------------------------------------
function DiarioDia({ k, s, wx, name, marks, onMark, onClose, onPrev, onNext, cfg, memo, feedId }) {
  const narrow = useNarrow();
  // Memoria del día: primero la plantilla; al abrir el día se pide la versión pulida (Claude Haiku, con caché).
  const [memoria, setMemoria] = useState(null);
  const base = memo ? memo.texto : '';
  useEffect(() => {
    let alive = true;
    setMemoria(base ? { texto: base, ia: false, cargando: true } : null);
    if (base && feedId) pulirMemoria(feedId, k, base, memo.datos).then((r) => alive && setMemoria({ ...r, cargando: false }));
    return () => { alive = false; };
  }, [k, base, feedId]); // eslint-disable-line react-hooks/exhaustive-deps
  const text = diarioText(name, k, s, wx, marks);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    const f = (e) => { if (e.key === 'Escape') onClose(); if (e.key === 'ArrowLeft') onPrev(); if (e.key === 'ArrowRight') onNext(); };
    window.addEventListener('keydown', f);
    return () => window.removeEventListener('keydown', f);
  }, [onClose, onPrev, onNext]);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); } catch (e) {
      const ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove();
    }
    setCopied(true); setTimeout(() => setCopied(false), 1800);
  };
  const title = `${cap(DOW_LONG[dowOf(k)])} ${+k.slice(8, 10)} de ${MONTHS[+k.slice(5, 7) - 1]}`;

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 1300, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-label={`Diario de obra ${title}`}
        style={{ width: narrow ? '100%' : 'min(640px, 100%)', height: '100%', background: T.bg, overflowY: 'auto', boxShadow: '-12px 0 40px rgba(0,0,0,.18)', color: T.text }}>
        <div style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff', borderBottom: `1px solid ${T.line}`, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Btn onClick={onPrev} title="Día anterior (←)">‹</Btn>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: T.muted, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 600 }}>Diario de obra</div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>{title}</div>
          </div>
          <Btn onClick={onNext} title="Día siguiente (→)">›</Btn>
          <Btn onClick={onClose} title="Cerrar (Esc)">✕</Btn>
        </div>

        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {memoria && (
            <section data-memoria-dia style={{ background: '#fff', border: `1px solid ${T.line}`, borderLeft: `4px solid ${T.blue}`, borderRadius: 14, padding: 14 }}>
              <div style={{ fontSize: 11, color: T.muted, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 600, marginBottom: 6 }}>Memoria del día</div>
              <div style={{ fontSize: 15, lineHeight: 1.55 }}>{memoria.texto}</div>
              <div style={{ fontSize: 11, color: T.muted, marginTop: 6 }}>
                {memoria.cargando ? 'Armada con los datos de la cámara…' : memoria.ia ? 'Redactada con IA a partir de los datos de la cámara (personas, entregas, máquinas, materiales y clima). Puede tener errores.' : 'Armada automáticamente con los datos de la cámara.'}
              </div>
            </section>
          )}
          {/* Resumen */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
            <Stat label="Jornada" value={s.first != null ? `${fmtHM(s.first)}–${fmtHM(s.last)}` : '—'} />
            <Stat label="Pico de personas" value={s.peak || '—'} />
            <Stat label="Con casco" value={s.casco != null ? `${s.casco}%` : '—'} />
            <Stat label="Clima" value={wx ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><WxBadge wx={wx} size={18} />{wx.label}</span> : '—'} sub={wx && wx.detail} />
          </div>

          <Section title="Actividad del día">
            <Curve s={s} cfg={cfg} />
          </Section>

          {MOSTRAR_ENTREGAS && <Section title={`${s.fuente === 'iaV3' ? 'Entregas' : 'Visitas de camión'}${s.visits.length ? ` (${s.visits.length})` : ''}`}>
            {!s.visits.length && <div style={{ color: T.muted, fontSize: 13 }}>{s.fuente === 'iaV3' ? 'Este día no se vieron entregas de material.' : 'No se vieron camiones entrando a la obra este día.'}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {s.visits.map((v) => <Visit key={v.id} v={v} mark={marks[v.id]} onMark={onMark} />)}
            </div>
            {s.parked.length > 0 && (
              <div style={{ fontSize: 12, color: T.muted, marginTop: 10 }}>
                Vehículo estacionado (no se cuenta como entrega): {s.parked.map((v) => `${vLabel(v.cls)} ${fmtHM(v.arrive)}–${fmtHM(v.leave)}`).join(' · ')}
              </div>
            )}
          </Section>}

          {s.best && (
            <Section title="Foto con más movimiento">
              <Photo photo={s.best} />
              <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>{fmtHM(s.best.ms)} h</div>
            </Section>
          )}

          <Section title="Para compartir">
            <div style={{ background: '#fff', border: `1px solid ${T.line}`, borderRadius: 10, padding: 12, fontSize: 14, lineHeight: 1.5 }}>{text}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
              <Btn active onClick={copy}>{copied ? '✓ Copiado' : '📋 Copiar texto'}</Btn>
              <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}><Btn>WhatsApp</Btn></a>
              <a href={`mailto:?subject=${encodeURIComponent(text.split(':')[0])}&body=${encodeURIComponent(text)}`} style={{ textDecoration: 'none' }}><Btn>Mail</Btn></a>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, sub }) => (
  <div style={{ background: '#fff', border: `1px solid ${T.line}`, borderRadius: 12, padding: '10px 12px' }}>
    <div style={{ fontSize: 11, color: T.muted }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{sub}</div>}
  </div>
);
const Section = ({ title, children }) => (
  <section style={{ background: '#fff', border: `1px solid ${T.line}`, borderRadius: 14, padding: 14 }}>
    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>{title}</div>
    {children}
  </section>
);

// Curva de personas por foto en la jornada, con las visitas de camión marcadas debajo
function Curve({ s, cfg }) {
  const W = 600, H = 96, pad = 18;
  const k = s.curve.length ? s.curve[0].ms : 0;
  const t0 = dayStartMs(new Date(k - 3 * 3600e3).toISOString().slice(0, 10)) + (cfg.onlyWork ? cfg.workFrom : 6) * 3600e3;
  const t1 = t0 + ((cfg.onlyWork ? cfg.workTo - cfg.workFrom : 14)) * 3600e3;
  const x = (ms) => pad + ((ms - t0) / (t1 - t0)) * (W - 2 * pad);
  const max = Math.max(1, ...s.curve.map((c) => c.persons));
  const y = (v) => H - 26 - (v / max) * (H - 36);
  const pts = s.curve.map((c) => `${x(c.ms).toFixed(1)},${y(c.persons).toFixed(1)}`);
  const area = pts.length ? `M${x(s.curve[0].ms)},${y(0)} L${pts.join(' L')} L${x(s.curve[s.curve.length - 1].ms)},${y(0)} Z` : '';
  const hours = [];
  for (let t = t0; t <= t1; t += 2 * 3600e3) hours.push(t);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label="Personas en obra a lo largo del día">
      <line x1={pad} x2={W - pad} y1={y(0)} y2={y(0)} stroke={T.line} />
      {area && <path d={area} fill="rgba(16,185,129,.18)" stroke={T.green} strokeWidth="1.5" />}
      {s.visits.map((v) => (
        <rect key={v.id} x={x(v.arrive)} y={H - 20} width={Math.max(4, x(v.leave + 10 * 60e3) - x(v.arrive))} height={8} rx={4} fill={vColor(v.cls)}>
          <title>{`${cap(vLabel(v.cls))} ${fmtHM(v.arrive)}–${fmtHM(v.leave)}`}</title>
        </rect>
      ))}
      {hours.map((t) => <text key={t} x={x(t)} y={H - 2} fontSize="10" fill={T.muted} textAnchor="middle">{fmtHM(t).slice(0, 2)}h</text>)}
      <text x={pad} y={10} fontSize="10" fill={T.muted}>{max} personas</text>
    </svg>
  );
}

// Entrega de iaV3 que llegó con la primera foto del día (el modelo no trae "antes"): se usa la última foto anterior
// a la llegada (del mismo día o del anterior, hasta 4 días antes) y se marca con su fecha.
function conAntes(v, photos) {
  if (!v.v3 || v.before || !photos.length) return v;
  const ref = v.arrival ? v.arrival.ms : v.arrive;
  if (!ref) return v;
  let lo = 0, hi = photos.length - 1, i = -1;
  while (lo <= hi) { const mid = (lo + hi) >> 1; if (photos[mid].ms < ref) { i = mid; lo = mid + 1; } else hi = mid - 1; }
  const p = i >= 0 ? photos[i] : null;
  if (!p || !p.s3 || ref - p.ms > 4 * 864e5) return v;
  return { ...v, before: { id: p.id, ms: p.ms, s3: p.s3, w: p.w || v.w, h: p.h || v.h }, antesOtroDia: dayKey(p.ms) !== v.day };
}

export function Visit({ v, mark, onMark }) {
  const [diff, setDiff] = useState(undefined);
  const [note, setNote] = useState(mark?.nota || '');
  useEffect(() => { setNote(mark?.nota || ''); }, [mark?.nota]);
  useEffect(() => {
    let alive = true;
    if (!v.v3) zoneChange(v).then((r) => alive && setDiff(r)); // iaV3 ya sabe qué material quedó
    return () => { alive = false; };
  }, [v]);
  const zone = v.sinCaja ? null : (() => {
    const bw = v.box.x2 - v.box.x1, bh = v.box.y2 - v.box.y1, cx = (v.box.x1 + v.box.x2) / 2, cy = (v.box.y1 + v.box.y2) / 2;
    return { x1: Math.max(0, cx - bw), y1: Math.max(0, cy - bh), x2: Math.min(v.w, cx + bw), y2: Math.min(v.h, cy + bh) };
  })();
  // Recorte 16:9 alrededor del vehículo (4 veces su ancho, como mínimo un tercio de la foto); clic = foto completa
  const [full] = useState(!!v.sinCaja);
  const crop = (() => {
    const bw = v.box.x2 - v.box.x1, cx = (v.box.x1 + v.box.x2) / 2, cy = (v.box.y1 + v.box.y2) / 2;
    let w = Math.min(v.w, Math.max(4 * bw, v.w / 3)), h = (w * 9) / 16;
    if (h > v.h) { h = v.h; w = (h * 16) / 9; }
    const x1 = Math.max(0, Math.min(v.w - w, cx - w / 2)), y1 = Math.max(0, Math.min(v.h - h, cy - h / 2));
    return { x1, y1, x2: x1 + w, y2: y1 + h };
  })();
  // Zona ampliada del visor: ~3 veces el vehículo (o 1,5 la zona si no hay caja), mínimo un sexto de la foto, en 16:9
  const zoomZona = (() => {
    const b = v.sinCaja ? zone : v.box, k = v.sinCaja ? 1.2 : 2;
    if (!b) return null;
    const cx = (b.x1 + b.x2) / 2, cy = (b.y1 + b.y2) / 2;
    let w = Math.min(v.w, Math.max(k * (b.x2 - b.x1), (k * (b.y2 - b.y1) * 16) / 9, v.w / 8)), h = (w * 9) / 16;
    if (h > v.h) { h = v.h; w = (h * 16) / 9; }
    const x1 = Math.max(0, Math.min(v.w - w, cx - w / 2)), y1 = Math.max(0, Math.min(v.h - h, cy - h / 2));
    return { x1, y1, x2: x1 + w, y2: y1 + h };
  })();
  const [visor, setVisor] = useState(null);
  const color = vColor(v.cls);
  const estado = mark?.estado;
  const headline = v.v3 ? tituloV3(v) : v.cls === 'Mixer' ? 'Hormigonado: llegó un mixer' : `Posible entrega: llegó un ${vLabel(v.cls)}`;
  const fotosVisor = [{ photo: v.before, label: 'Antes', box: null, zone }, { photo: v.arrival, label: v.tipo === 'material' ? 'Apareció' : 'Llegada', box: v.sinCaja ? null : { ...v.box, color }, zone: null }, { photo: v.after, label: 'Después', box: null, zone }];
  const thumb = (photo, label, box, z, idx) => (
    <div key={label} style={{ minWidth: 0 }}>
      {photo ? <Photo photo={photo} box={box} zone={z} crop={full ? null : crop} onClick={() => setVisor(idx)} style={{ cursor: 'zoom-in' }} /> : <div style={{ aspectRatio: '16 / 9', borderRadius: 8, background: '#f3f4f6', display: 'grid', placeItems: 'center', fontSize: 11, color: T.muted, textAlign: 'center', padding: 4 }}>{idx === 0 ? 'No hay una foto anterior' : 'Sin foto'}</div>}
      <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>{label}{photo ? ` · ${idx === 0 && v.antesOtroDia ? `${fmtDM(photo.ms)} ` : ''}${fmtHM(photo.ms)}` : ''}</div>
    </div>
  );
  return (
    <div style={{ border: `1px solid ${estado === 'no' ? T.line : color + '55'}`, borderLeft: `4px solid ${estado === 'no' ? '#d1d5db' : color}`, borderRadius: 12, padding: 12, opacity: estado === 'no' ? 0.6 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>{headline}</div>
        <div style={{ fontSize: 13, color: T.muted }}><b style={{ color: T.text }}>{v.tipo === 'material' ? `${fmtHM(v.arrive)} h` : `${fmtHM(v.arrive)} → ${fmtHM(v.leave)}`}</b>{v.tipo !== 'material' && <> · {v.mins} min</>}</div>
      </div>
      {v.v3 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6, alignItems: 'center' }}>
          {v.materiales.map((m) => (
            <span key={m.cls} title={`${m.n} ${m.n === 1 ? 'objeto nuevo' : 'objetos nuevos'} · ${(m.area * 100).toFixed(1).replace('.', ',')} % de la foto`}
              style={{ fontSize: 12, fontWeight: 600, background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', borderRadius: 999, padding: '2px 8px' }}>
              {MATERIAL_ICON[m.cls]} {MATERIAL_LABEL[m.cls] || m.cls}
            </span>
          ))}
          {!v.materiales.length && !(v.carga || []).length && v.tipo === 'vehiculo' && v.cls !== 'Mixer' && <span style={{ fontSize: 12, color: T.muted }}>En las fotos no se ve que haya dejado material. Puede haber retirado algo o descargado fuera de la cámara.</span>}
          {v.sinConfirmar && !estado && <span title="La detectó la IA y todavía nadie la revisó: tocá Confirmar entrega o Descartar" style={{ fontSize: 11, fontWeight: 600, background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', borderRadius: 999, padding: '1px 8px', marginLeft: 'auto' }}>Falta revisar</span>}
          <span style={{ fontSize: 11, color: T.muted, marginLeft: v.sinConfirmar && !estado ? 0 : 'auto' }} title="Qué tan segura está la IA de que fue una entrega">Seguridad de la IA: {v.confianza}</span>
        </div>
      )}
      {v.v3 && v.juez && v.juez.descripcion && (
        <div style={{ fontSize: 12, color: T.muted, marginTop: 6 }} title="Revisión visual de la foto de llegada (ampliada) y la de después">👁 {v.juez.descripcion}</div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8, marginTop: 10 }}>
        {thumb(v.before, 'Antes', null, zone, 0)}
        {thumb(v.arrival, v.tipo === 'material' ? 'Apareció' : 'Llegada', v.sinCaja ? null : { ...v.box, color }, null, 1)}
        {thumb(v.after, 'Después', null, zone, 2)}
      </div>
      <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>Tocá una foto para verla en grande y acercar la zona.</div>
      {visor != null && <Visor fotos={fotosVisor} inicio={visor} zoomZona={zoomZona} onClose={() => setVisor(null)} />}
      {diff && diff.tight != null && diff.tight < 6 && v.before && v.arrival.ms - v.before.ms >= 5 * 60e3 && !estado && (
        <div style={{ fontSize: 12, marginTop: 6, color: '#92400e' }}>En la foto de llegada no se ve nada nuevo en esa zona: revisá si fue una entrega.</div>
      )}
      {diff && diff.changed != null && (
        <div style={{ fontSize: 12, marginTop: 6, color: diff.changed ? '#047857' : T.muted }}>
          {diff.changed ? '● La zona donde estuvo el camión cambió después de la visita (puede haber quedado material).' : '○ Después de la visita, la zona quedó igual que antes.'}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
        <Btn active={estado === 'si'} onClick={() => onMark(v, { estado: estado === 'si' ? null : 'si' })}>✓ Confirmar entrega</Btn>
        <Btn active={estado === 'no'} onClick={() => onMark(v, { estado: estado === 'no' ? null : 'no' })} style={estado === 'no' ? { background: '#6b7280', borderColor: '#6b7280' } : null}>{v.v3 ? 'Descartar' : 'No fue entrega'}</Btn>
        <input value={note} onChange={(e) => setNote(e.target.value)} onBlur={() => note !== (mark?.nota || '') && onMark(v, { nota: note.trim() })}
          onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
          placeholder="Qué llegó: ladrillos, arena, hierro…" style={{ ...inputStyle, flex: 1, minWidth: 160 }} />
      </div>
    </div>
  );
}

// Para "Preguntale a tu obra": entregas por día (sin las descartadas)
function entregasParaChat(sums, marks) {
  const out = {};
  for (const [k, s] of sums) {
    const vs = s.visits.filter((v) => marks[v.id]?.estado !== 'no');
    if (vs.length) out[k] = vs.map((v) => ({ v, confirmada: marks[v.id]?.estado === 'si', nota: marks[v.id]?.nota || '' }));
  }
  return out;
}

